/**
 * Past runs: the account's kept simulator runs in a sheet beside the simulator, newest first,
 * loading more as the list is scrolled. A picked run previews its thread, reply and tool
 * calls; opening it puts that run back on screen.
 */

import { useMemo, useRef, useState } from 'react';
import { useAtomValue } from 'jotai';
import { History, Loader2, MessagesSquare } from 'lucide-react';
import { Sheet, SheetContent, SheetDescription, SheetFooter, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Switch } from '@/components/ui/switch';
import { currentUserAtom } from '@/hooks/use-auth';
import { useInfiniteScrollSentinel } from '@/hooks/use-infinite-scroll-sentinel';
import { MODE_OPTIONS } from '@/modules/back-testing/runs/modes';
import { storedRunFacts } from '@/modules/back-testing/runs/run-facts';
import { RunThread } from '@/modules/back-testing/runs/run-thread';
import type { SimulatorRun, SimulatorRunFilter } from '@/services/simulator-run.service';
import { fmtWhen } from '../conversations/conversation-format';
import { PanelState } from '../ui/panel-state';
import { PillButton } from '../ui/pill-button';
import { Segmented, type SegmentedOption } from '../ui/segmented';
import { PastRunRow } from './past-run-row';
import { useRunAuthor } from './use-run-author';
import { useSimulatorRuns } from './use-simulator-runs';

type ModeFilter = 'all' | NonNullable<SimulatorRunFilter['mode']>;

const MODE_FILTER_OPTIONS: SegmentedOption<ModeFilter>[] = [{ value: 'all', label: 'All' }, ...MODE_OPTIONS];

const RunPreview = ({ run }: { run: SimulatorRun }) => {
  const [verdictOpen, setVerdictOpen] = useState(true);
  const [reportOpen, setReportOpen] = useState(false);
  return (
    <div className='flex flex-1 flex-col overflow-y-auto p-6'>
      <RunThread
        run={storedRunFacts(run)}
        detail={run}
        queryTurns={null}
        active={false}
        trail={[]}
        investigationTrail={undefined}
        verdictOpen={verdictOpen}
        onVerdictOpenChange={setVerdictOpen}
        reportOpen={reportOpen}
        onReportOpenChange={setReportOpen}
      />
    </div>
  );
};

interface PastRunsBodyProps {
  /** A run is in flight; opening another would replace it mid-answer. */
  busy: boolean;
  onOpen: (run: SimulatorRun) => void;
}

const PastRunsBody = ({ busy, onOpen }: PastRunsBodyProps) => {
  const currentUser = useAtomValue(currentUserAtom);
  const [mode, setMode] = useState<ModeFilter>('all');
  const [mine, setMine] = useState(false);
  const filter = useMemo<SimulatorRunFilter>(
    () => ({
      ...(mode !== 'all' && { mode }),
      ...(mine && currentUser && { userId: String(currentUser.id) }),
    }),
    [mode, mine, currentUser]
  );
  const { runs, isLoading, error, hasMore, loadingMore, loadMore } = useSimulatorRuns(filter);
  const authorOf = useRunAuthor();
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const selected = runs.find((run) => run.requestId === selectedId) ?? null;
  const listRef = useRef<HTMLDivElement | null>(null);
  const sentinelRef = useInfiniteScrollSentinel({
    hasNextPage: hasMore && !error,
    isFetchingNextPage: loadingMore,
    onLoadMore: loadMore,
    rootRef: listRef,
  });

  return (
    <>
      {/* Right padding keeps the filters clear of the sheet's close button. */}
      <SheetHeader className='flex-row items-center justify-between gap-4 border-b border-gray-500 py-3 pr-12 pl-4'>
        <div className='min-w-0'>
          <SheetTitle className='text-[15px] text-text-heading'>Past runs</SheetTitle>
          <SheetDescription className='text-xs text-gray-800'>
            Every Ask AI in the simulator on this account, kept when it ended.
          </SheetDescription>
        </div>
        <div className='flex shrink-0 items-center gap-4'>
          <Segmented options={MODE_FILTER_OPTIONS} value={mode} onChange={setMode} />
          <label className='flex items-center gap-2 text-xs text-text-body'>
            <Switch checked={mine} onCheckedChange={setMine} />
            Only my runs
          </label>
        </div>
      </SheetHeader>

      <div className='flex min-h-0 flex-1'>
        <div className='flex w-[30%] min-w-[260px] shrink-0 flex-col border-r border-gray-500'>
          <div ref={listRef} className='flex min-h-0 flex-1 flex-col overflow-y-auto'>
            {isLoading ? (
              <PanelState icon={<Loader2 className='h-4 w-4 animate-spin' />}>Loading runs</PanelState>
            ) : runs.length === 0 ? (
              <PanelState icon={<History className='h-5 w-5' />}>
                {error ?? (mode !== 'all' || mine ? 'No runs match these filters.' : 'No runs kept yet.')}
              </PanelState>
            ) : (
              <>
                {runs.map((run) => (
                  <PastRunRow
                    key={run.requestId}
                    run={run}
                    author={authorOf(run.origin.userId)}
                    selected={run.requestId === selectedId}
                    onSelect={() => setSelectedId(run.requestId)}
                  />
                ))}
                {error && <p className='px-4 py-3 text-center text-xs text-red-700'>{error}</p>}
                {loadingMore && (
                  <div className='flex justify-center py-3'>
                    <Loader2 className='h-4 w-4 animate-spin text-gray-800' />
                  </div>
                )}
                <div ref={sentinelRef} aria-hidden='true' className='h-px shrink-0' />
              </>
            )}
          </div>
        </div>

        <div className='flex min-h-0 flex-1 flex-col'>
          {selected ? (
            <RunPreview key={selected.requestId} run={selected} />
          ) : (
            <PanelState icon={<MessagesSquare className='h-5 w-5' />}>Select a run to preview it.</PanelState>
          )}
        </div>
      </div>

      <SheetFooter className='flex-row items-center justify-between border-t border-gray-500 px-4 py-3'>
        <span className='truncate text-xs text-gray-800'>
          {busy
            ? 'A run is still in progress'
            : selected && `${authorOf(selected.origin.userId)} · ${fmtWhen(selected.createdAt)}`}
        </span>
        <PillButton
          variant='primary'
          className='h-8 px-4'
          disabled={!selected || busy}
          onClick={() => selected && onOpen(selected)}
        >
          Open in simulator
        </PillButton>
      </SheetFooter>
    </>
  );
};

interface PastRunsSheetProps extends PastRunsBodyProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const PastRunsSheet = ({ open, onOpenChange, ...body }: PastRunsSheetProps) => (
  <Sheet open={open} onOpenChange={onOpenChange}>
    <SheetContent side='right' className='flex w-[70vw] flex-col gap-0 bg-background p-0 backdrop-blur-none sm:max-w-none'>
      <PastRunsBody {...body} />
    </SheetContent>
  </Sheet>
);
